import PropTypes from 'prop-types';
import { Box, GlobalStyles } from '@mui/material';

/**
 * TableShell — a reusable wrapper that visually links a data table to the
 * widget/card filter currently driving it.
 *
 * When (and only when) a widget filter is active, it renders a thin,
 * slow-moving colour "trace" around the table border, in the exact colour of
 * the active widget — so the trace reads as guidance ("this table is being
 * guided by this widget"), not a warning.
 *
 * ISS-037: a "<label> active" chip naming the filter (for sighted users on an
 * empty board) used to render here, above the table. It has since moved into
 * the table's own filter toolbar, to the left of the search box — see
 * `ActiveFilterChip` (./ActiveFilterChip.jsx), rendered by callers such as
 * `NewLead.jsx`. TableShell now only owns the trace + the aria-label below;
 * it renders no visible chip of its own. The way OUT of the filter stays on
 * the widget card itself ("Clear to see all", ISS-036), so there is exactly
 * one place to click to undo it.
 *
 * No active filter → renders children untouched (no trace).
 *
 * Usage:
 *   <TableShell activeFilterLabel={w?.label} activeFilterColor={w?.color}>
 *     <NewLeadTable />
 *   </TableShell>
 *
 * Deliberately subtle per the UX contract: thin line, short segment, slow
 * movement, no glow, no flashing.
 */

const RADIUS = 16; // matches the table Card (theme.shape.borderRadius * 2)

// Registered once. `--tableshell-angle` is an animatable <angle> custom
// property; the keyframes spin it, which sweeps the conic-gradient segment
// around the border. Browsers without @property support degrade gracefully to
// a static segment (no motion) — still a valid, subtle indicator.
const traceGlobalStyles = (
    <GlobalStyles
        styles={{
            '@property --tableshell-angle': {
                syntax: '"<angle>"',
                inherits: 'false',
                initialValue: '0deg',
            },
            '@keyframes tableshell-trace-spin': {
                to: { '--tableshell-angle': '360deg' },
            },
        }}
    />
);

export default function TableShell({ activeFilterLabel, activeFilterColor, children }) {
    const active = Boolean(activeFilterLabel && activeFilterColor);

    if (!active) {
        return children;
    }

    return (
        <>
            {traceGlobalStyles}
            {/* Trace wrapper — the moving segment lives on a masked ::before ring. */}
            <Box
                sx={{
                    position: 'relative',
                    borderRadius: `${RADIUS}px`,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 'inherit',
                        padding: '1.5px', // thin line
                        // Short (~45°) coloured arc on an otherwise transparent ring.
                        background: `conic-gradient(from var(--tableshell-angle, 0deg), transparent 0deg, transparent 315deg, ${activeFilterColor} 350deg, transparent 360deg)`,
                        // Mask the fill so only the 1.5px ring shows (border, not body).
                        WebkitMask:
                            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        maskComposite: 'exclude',
                        animation: 'tableshell-trace-spin 7s linear infinite', // slow
                        pointerEvents: 'none',
                        zIndex: 2,
                    },
                }}
                aria-label={`Table filtered by ${activeFilterLabel}`}
            >
                {children}
            </Box>
        </>
    );
}

TableShell.propTypes = {
    // The active widget's label, e.g. 'Due Today'. Falsy → no trace/chip.
    activeFilterLabel: PropTypes.string,
    // The active widget's colour (hex), e.g. '#23a36d'. Falsy → no trace/chip.
    activeFilterColor: PropTypes.string,
    children: PropTypes.node,
};

TableShell.defaultProps = {
    activeFilterLabel: null,
    activeFilterColor: null,
    children: null,
};
